import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  input,
  OnInit,
  signal,
  ViewChild,
  ChangeDetectionStrategy,
  inject
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  AwBadgeComponent,
  AwBreadCrumbComponent,
  AwButtonDirective,
  AwDialogComponent,
  AwDividerComponent,
  AwFormFieldLabelComponent,
  AwSearchComponent,
  AwSelectMenuComponent,
  AwSpinnerComponent,
  AwTableComponent,
  AwToastComponent,
  BreadCrumb,
  DialogFormControls,
  DialogOptions,
  SelectedOption,
  TableCellInput,
  TableActionMenu,
  TableCellTypes,
  TableRow,
  ToastVariants,
  SingleSelectOption,
} from '@assetworks-llc/aw-component-lib';
import { IntegrationService } from '../../../../../core/services/integration.service';
import { CustomerService } from '../../../../../core/services/customer.service';
import { Customer, Integration } from '../../../../../api/models';
import {
  catchError,
  finalize,
  forkJoin,
  map,
  Observable,
  of,
  switchMap,
  take,
} from 'rxjs';
import { UpdateOrganizationParams } from '../../../../../types/types';
import { MatIcon } from '@angular/material/icon';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  Organization,
  UserService,
} from '../../../../../core/services/user.service';
import {ClientSecretService} from '../../../../../core/services/client-secret.service';
import { ApiAccessSectionComponent } from '../../../../../shared/components/api-access-section/api-access-section.component';
import { AwDropdownFormComponent } from '../../../../../shared/components/aw-dropdown-form/aw-dropdown-form.component';
import { linkCellTemplate } from '../../../../../shared/utils/link-cell-template';
import { TableActionBarComponent } from '../../../../../shared/components/table-action-bar/table-action-bar.component';
import {
  ASSOCIATED_FILTER_OPTIONS,
  DEFAULT_ASSOCIATED_FILTER,
  filterByAssociation,
} from '../../../../../shared/utils/table-filters';

/**
 * Pure function: builds dropdown options from table data and associated customer IDs.
 * Exported for property-based testing.
 */
export function buildAssociatedCustomerOptions(
  tableData: any[],
  customerIds: string[] | undefined
): SingleSelectOption[] {
  const idSet = new Set(customerIds ?? []);
  return tableData
    .filter((row: any) => idSet.has(row.customerInfo.customerId))
    .map((row: any) => ({
      label: row.customerInfo.customerName,
      value: row.customerInfo.customerId,
    }));
}

import {
  associationBadge,
  associationRowActions,
  CustomerRow,
  statusBadge,
} from '../../../../../shared/utils/association-table';

@Component({
    selector: 'app-customer-details',
    imports: [
        CommonModule,
        AwBreadCrumbComponent,
        AwDividerComponent,
        AwSearchComponent,
        AwTableComponent,
        AwSpinnerComponent,
        AwDialogComponent,
        AwToastComponent,
        AwButtonDirective,
        AwSelectMenuComponent,
        AwFormFieldLabelComponent,
        MatIcon,
        FormsModule,
        ReactiveFormsModule,
        ApiAccessSectionComponent,
        AwDropdownFormComponent,
        TableActionBarComponent,
    ],
    templateUrl: './integration-details.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: [
        './integration-details.component.scss',
        '../../../../../../styles/management.scss',
    ]
})
export class IntegrationDetailsComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly customerService = inject(CustomerService);
  private readonly integrationService = inject(IntegrationService);
  private readonly userService = inject(UserService);
  private readonly clientSecretService = inject(ClientSecretService);

  AuthTypeMap = {
    0: 'OAuth2',
    1: 'EntraID',
    2: 'OpenID Connect',
    3: 'N/A',
  } as const;

  constructor() {
    // CCL's multi-select re-emits the same array it mutates, so filter on a copy.
    this.associatedFilter.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.applyAssociatedFilter());
  }

  @ViewChild(AwToastComponent) toastComponent!: AwToastComponent;
  @ViewChild(AwTableComponent, { static: true })
  awTableComponent!: AwTableComponent;

  isLoading: boolean = false;
  openDeleteCustomerDialogSingle: boolean = false;
  openChangeCustomerStatus: boolean = false;
  openAssociateCustomerDialog: boolean = false;

  customersInfo: any[] = [];
  singleCustomerToDelete: TableRow | null = null;
  multipleCustomersToDelete: TableRow[] | null = null;

  userPermissions: Record<string, boolean> = {};
  organization: Organization | null = null;

  integrationInfo!: Integration | undefined;
  integrationInfoDividers = signal<any[]>([]);
  integrationDateDividers = signal<any[]>([]);

  customersTableData = signal<CustomerRow[]>([]);
  customersTableDataCopy: CustomerRow[] = [];
  disableCustomerChangeStatus: boolean = true;
  disableCustomerAssociate: boolean = true;
  customerSearchValue!: string;
  checkedRows: TableRow[] = [];
  /** The rows the Associate dialog will add: checked rows from the bar, or one row from its menu. */
  rowsToAssociate: TableRow[] = [];

  readonly associatedFilterOptions = ASSOCIATED_FILTER_OPTIONS;
  associatedFilter = new FormControl<SelectedOption[]>([...DEFAULT_ASSOCIATED_FILTER]);

  generatedClientSecret: string = '';
  generatedClientId: string = '';

  selectedCustomerId: string = '';
  associatedCustomerOptions = signal<SingleSelectOption[]>([]);
  hasAssociatedCustomers = computed<boolean>(() => this.associatedCustomerOptions().length > 0);

  dialogOptionsDeleteSingle: DialogOptions = {
    variant: 'alert',
    title: `Change Association`,
    description:
      'By selecting Continue, you will disassociate the selected customer from this Third-Party Integration. Choose Cancel, to go back to the view.',
    secondaryButtonLabel: 'Cancel',
    primaryButtonLabel: 'Continue',
  };

  dialogOptionsChangeStatus: DialogOptions = {
    variant: 'standard',
    title: `Change Customer Status`,
    secondaryButtonLabel: 'Cancel',
    primaryButtonLabel: 'Change',
  };

  dialogOptionsAssociate: DialogOptions = {
    variant: 'alert',
    title: 'Associate Customers',
    description: 'By selecting Continue, you will associate the selected customer(s) with this Third-Party Integration. Choose Cancel, to go back to the view.',
    secondaryButtonLabel: 'Cancel',
    primaryButtonLabel: 'Continue',
  };

  changeStatusFormFields = input<DialogFormControls>({
    controls: [
      {
        formControlName: 'changeStatus',
        label: 'Change Status',
        controlRequired: true,
        value: '',
        type: 'select',
        selectOptions: {
          enableSearch: false,
          singleSelectOptions: [
            { label: 'Active', value: true },
            { label: 'Disabled', value: false },
          ],
        },
        validators: {
          required: {
            value: true,
            message: 'Please Enter a value',
          },
        },
      },
    ],
  });

  headerButtonActions = signal<any[]>([
    {
      iconButtonCallback: {
        icon: 'edit',
        label: 'Edit third-party integration',
        action: () => {
          this.router.navigate(['edit'], { relativeTo: this.route });
        },
      },
    },
  ]);

  customersColumnsDefinition: TableCellInput[] = [
    {
      align: 'left',
      type: TableCellTypes.Custom,
      isExpanded: false,
      // CCL sorts a combined column by its key (table.mdx, 'custom columns that use combineFields'), so the key is the
      // displayed name; an object or missing field left sorting dead.
      key: 'customerInfo.customerName',
      label: 'Customer Name/ID',
      combineFields: [
        'customerInfo.customerName',
        'customerInfo.detailsPageRoute',
        'customerInfo.customerId',
      ],
      combineTemplate: (values: any[]) => ({
        template: linkCellTemplate(values[0], values[1], values[2]),
      }),
      sort: true,
    },
    {
      type: TableCellTypes.Custom,
      isExpanded: false,
      key: 'Associated',
      label: 'Associated',
      combineFields: ['associationBadge.color', 'associationBadge.label'],
      combineTemplate: (values: any[]) => ({
        component: AwBadgeComponent,
        componentData: {
          color: values[0],
          label: values[1],
        },
      }),
      align: 'center',
    },
    {
      type: TableCellTypes.Custom,
      isExpanded: false,
      key: 'Status',
      combineFields: ['badgeNested.color', 'badgeNested.label'],
      combineTemplate: (values: any[]) => ({
        component: AwBadgeComponent,
        componentData: {
          color: values[0],
          label: values[1],
        },
      }),
      align: 'center',
    },
  ];

  breadcrumbs = signal<BreadCrumb[]>([]);

  private rebuildAssociatedCustomerOptions(): void {
    this.associatedCustomerOptions.set(
      buildAssociatedCustomerOptions(this.customersTableDataCopy, this.integrationInfo?.customerIds)
    );
  }

  /** Shows the rows matching the Associated filter. The table keeps its search text across data changes. */
  applyAssociatedFilter(): void {
    this.customersTableData.set(
      filterByAssociation(this.customersTableDataCopy, [...(this.associatedFilter.value ?? [])])
    );
  }

  private toCustomerRow(customer: Customer | null | undefined): CustomerRow {
    const isAssociated = this.integrationInfo?.customerIds?.includes(customer?.organizationId ?? '') ?? false;
    return {
      customerInfo: {
        customerName: customer?.organizationName ?? '',
        customerId: customer?.organizationId ?? '',
        customerIntegrationPartnerIDs: customer?.integrationPartnerIds ?? [],
        detailsPageRoute: `/customer-management/customer-details/${customer?.organizationId}`,
      },
      associationBadge: associationBadge(isAssociated),
      badgeNested: statusBadge(customer?.enabled ?? false),
      authType: customer?.authenticationType,
    };
  }

  onCustomerSelected(option: SingleSelectOption | null): void {
    if (!option) {
      this.selectedCustomerId = '';
      return;
    }
    const newId = option.value as string;
    if (newId === this.selectedCustomerId) {
      return;
    }
    this.selectedCustomerId = '';
    setTimeout(() => {
      this.selectedCustomerId = newId;
    });
  }

  buildAllData(): void {
    this.isLoading = true;
    this.userPermissions = this.userService.getUserPermissions();

    this.route.paramMap
      .pipe(
        map((params) => {
          this.breadcrumbs.set([
            ...this.breadcrumbs(),
            {
              label: 'Third-Party Integration Details',
              route: `/integration-management/integration-details/${params.get(
                'integrationId'
              )!}`,
              newTab: false,
            },
          ]);
          return params.get('integrationId')!;
        }),
        take(1),
        switchMap((integrationId) =>
          this.integrationService.getIntegrationById({
            organizationId: integrationId,
          })
        ),
        switchMap((integrationInfo) => {
          if (!integrationInfo) {
            return of([]);
          }

          this.integrationInfo = integrationInfo;

          this.integrationDateDividers.set([
            {
              title: 'Created Date',
              info: this.integrationInfo?.createdOn
                ? new Date(this.integrationInfo?.createdOn).toLocaleDateString()
                : 'N/A',
            },
            {
              title: 'Last Modified Date',
              info: this.integrationInfo?.modifiedOn
                ? new Date(
                    this.integrationInfo?.modifiedOn
                  ).toLocaleDateString()
                : 'N/A',
            },
          ]);

          // For integration employees, fetch only associated customers
          // For AssetWorks employees, fetch all customers
          if (this.organization === 'integration-employee') {
            const customerIds = integrationInfo.customerIds || [];
            if (customerIds.length === 0) {
              return of([]);
            }
            return forkJoin(
              customerIds.map(id =>
                this.customerService.getCustomerById({ organizationId: id })
                  .pipe(catchError(() => of(null)))
              )
            ).pipe(
              map(customers => customers.filter((c): c is Customer => c !== null))
            );
          } else {
            return this.customerService.getAllCustomers().pipe(
              catchError((error) => {
                this.buildToastMessage('Failed to load customers', 'error');
                return of([]);
              }),
              map((customers) => customers || [])
            );
          }
        }),
        switchMap((customers) => {
          if (this.integrationInfo?.organizationId && this.userPermissions['isAdministrator']) {
            return this.clientSecretService
              .getClientSecrets({
                organizationId: this.integrationInfo.organizationId
              })
              .pipe(
                map((secrets) => ({ customers, secrets })),
                catchError(() => of({ customers, secrets: undefined }))
              );
          }
          return of({ customers, secrets: undefined });
        }),
        catchError((error) => {
          this.buildToastMessage('Failed to load third-party integration data', 'error');
          return of({ customers: [], secrets: undefined });
        }),
        finalize(() => {
          this.isLoading = false;
        })
      )
      .subscribe(({ customers, secrets }) => {
        // Build Info Display
        const infoDividers = [
          {
            title: 'Third-Party Integration Name',
            info:
              this.integrationInfo?.organizationName ?? 'Unable to Retrieve',
          },
          {
            title: 'Integration ID',
            info:
              this.integrationInfo?.organizationId ?? 'Unable to Retrieve',
          },
          {
            title: 'Status',
            info: this.integrationInfo?.enabled ? 'Active' : 'Disabled',
          },
        ];

        if (this.organization === 'assetworks-employee' && this.userPermissions['isAdministrator']) {
          infoDividers.push({
            title: 'Client Secret Expiration Date',
            info: secrets && Object.keys(secrets).length > 0
              ? (() => {
                  const dates = Object.values(secrets).map(date => new Date(date)).filter(date => !isNaN(date.getTime()));
                  return dates.length > 0
                    ? new Date(Math.max(...dates.map(date => date.getTime()))).toLocaleDateString()
                    : 'No valid expiration dates';
                })()
              : 'No client secret expiration dates found',
          });
        }

        this.integrationInfoDividers.set(infoDividers);

        // Build Table
        this.customersTableDataCopy = customers?.map((customer) => this.toCustomerRow(customer)) ?? [];
        this.applyAssociatedFilter();
        this.rebuildAssociatedCustomerOptions();
      });
  }

  ngOnInit(): void {
    this.userPermissions = this.userService.getUserPermissions();
    this.organization = this.userService.getOrganization();

    if (this.organization === 'assetworks-employee') {
      this.breadcrumbs.set([
        ...this.breadcrumbs(),
        {
          label: 'Third-Party Integration Management',
          route: '/integration-management/overview',
          newTab: false,
        },
      ]);
    }

    if (this.organization === 'integration-employee') {
      const associatedColumnIndex = this.customersColumnsDefinition.findIndex(
        col => col.key === 'Associated'
      );
      if (associatedColumnIndex !== -1) {
        this.customersColumnsDefinition.splice(associatedColumnIndex, 1);
      }
    }

    if (this.organization === 'assetworks-employee' && (this.userPermissions['canDelete'] || this.userPermissions['canEdit'])) {
      this.customersColumnsDefinition.unshift({
        type: TableCellTypes.Checkbox,
        key: 'checkbox',
        isExpanded: false,
        align: 'center',
      });
    }

    this.buildAllData();
  }

  checkBoxActionHandler(checkedRows: { rows: TableRow[]; key: string }) {
    this.checkedRows = checkedRows.rows;
    const associatedRows = this.checkedRows.filter(row => row['associationBadge']?.isAssociated);
    const nonAssociatedRows = this.checkedRows.filter(row => !row['associationBadge']?.isAssociated);

    // Status belongs to the customer, not the association, so any checked row can change it (as in the row menu).
    this.disableCustomerChangeStatus = this.checkedRows.length === 0;
    this.disableCustomerAssociate = nonAssociatedRows.length === 0;
    this.multipleCustomersToDelete = associatedRows;
  }
  onSearchTable(searchInput: string) {
    this.awTableComponent.applyFilter(searchInput);
  }
  dialogFormCloseHandler(key: string) {
    if (key === 'delete') {
      this.openDeleteCustomerDialogSingle = false;
    } else if (key === 'change-status') {
      this.openChangeCustomerStatus = false;
    } else if (key === 'associate') {
      this.openAssociateCustomerDialog = false;
    }
  }

  buildToastMessage(message: string, variant: ToastVariants): void {
    this.toastComponent.showToast({
      variant: variant,
      title: message,
      description: '',
      removeIcon: true,
      removeX: false,
      alwaysShow: false,
      toastPositionTopLeft: false,
      multiToasts: false,
    });
  }

  deleteCustomerFormSubmit(): void {
    if (this.singleCustomerToDelete === null) {
      this.buildToastMessage('No customer selected for deletion', 'error');
      return;
    }

    this.isLoading = true;

    const removedCustomerId: string =
      this.singleCustomerToDelete['customerInfo'].customerId;
    const newCustomerIds: string[] = (
      this.integrationInfo?.customerIds ?? []
    ).filter((customerId) => customerId !== removedCustomerId);

    const integrationParams: UpdateOrganizationParams = {
      organizationAuthType: this.integrationInfo?.authenticationType,
      organizationName: this.integrationInfo?.organizationName ?? '',
      organizationStatus: this.integrationInfo?.enabled || false,
      organizationPartnerIds: newCustomerIds,
      organizationId: this.integrationInfo?.organizationId || '',
    };
    this.integrationService
      .updateIntegration(integrationParams)
      .subscribe((removed) => {
        if (removed) {
          this.buildToastMessage('Successfully disassociated customer', 'success');
          
          // Update integration info
          this.integrationInfo!.customerIds = newCustomerIds;
          
          // Update table data to show disassociation
          const customerToUpdate = this.customersTableDataCopy.find(
            (customer) => customer['customerInfo'].customerId === removedCustomerId
          );
          if (customerToUpdate) {
            Object.assign(customerToUpdate.associationBadge, associationBadge(false));
          }
          this.applyAssociatedFilter();
          this.rebuildAssociatedCustomerOptions();
          if (removedCustomerId === this.selectedCustomerId) {
            this.selectedCustomerId = '';
          }
        } else {
          this.buildToastMessage('Failed to disassociate customer', 'error');
        }
        this.openDeleteCustomerDialogSingle = false;
        this.isLoading = false;
      });
  }

  changeCustomerStatusFormSubmit(formValues: any): void {
    if (!formValues.valid) {
      this.buildToastMessage('Please select a status', 'alert');
      return;
    }
    if (!this.checkedRows.length) {
      this.buildToastMessage('No rows selected', 'error');
      return;
    }
    const status: boolean = formValues.formData.changeStatus.value;

    const state = status ? 'Active' : 'Disabled';
    const customersToChange = this.checkedRows.filter(
      (r) => r['badgeNested'].enabled !== status
    );
    const skipped = this.checkedRows.length - customersToChange.length;
    if (!customersToChange.length) {
      this.buildToastMessage(`The selected customers are already ${state}`, 'alert');
      return;
    }

    this.isLoading = true;

    const statusChangeRequests: Observable<boolean>[] = customersToChange.map(
      (customer) => {
        const customerInfo = customer['customerInfo'];
        const customerParams: UpdateOrganizationParams = {
          organizationAuthType: customer['authType'],
          organizationName: customerInfo.customerName,
          organizationStatus: status,
          organizationPartnerIds: customerInfo.customerIntegrationPartnerIDs,
          organizationId: customerInfo.customerId,
        };

        return this.customerService
          .updateCustomer(customerParams)
          .pipe(catchError(() => of(false)));
      }
    );

    forkJoin(statusChangeRequests)
      .pipe(
        finalize(() => {
          this.isLoading = false;
          this.openChangeCustomerStatus = false;
        })
      )
      .subscribe((results) => {
        const numStatusUpdates: number = results.filter(
          (updated) => updated
        ).length;

        customersToChange.forEach((customer, index) => {
          if (results[index]) {
            Object.assign(customer['badgeNested'], statusBadge(status));
          }
        });
        this.applyAssociatedFilter();

        const skippedNote = skipped ? ` ${skipped} already ${state}.` : '';
        if (numStatusUpdates === customersToChange.length) {
          this.buildToastMessage(
            skipped
              ? `Set ${numStatusUpdates} of ${this.checkedRows.length} customers to ${state}.${skippedNote}`
              : `Set ${numStatusUpdates} customers to ${state}`,
            'success'
          );
        } else if (numStatusUpdates > 0) {
          this.buildToastMessage(
            `Set ${numStatusUpdates} of ${this.checkedRows.length} customers to ${state}. ${
              customersToChange.length - numStatusUpdates
            } failed.${skippedNote}`,
            'alert'
          );
        } else {
          this.buildToastMessage(`Failed to set the selected customers to ${state}`, 'error');
        }
      });
  }

  associateCustomersFormSubmit(): void {
    const nonAssociatedCustomers = this.rowsToAssociate;

    if (!nonAssociatedCustomers.length) {
      this.buildToastMessage('No non-associated customers selected', 'error');
      return;
    }

    this.isLoading = true;

    const customerIdsToAdd = nonAssociatedCustomers.map(c => c['customerInfo'].customerId);
    const newCustomerIds = [...(this.integrationInfo?.customerIds ?? []), ...customerIdsToAdd];

    const integrationParams: UpdateOrganizationParams = {
      organizationAuthType: this.integrationInfo?.authenticationType,
      organizationName: this.integrationInfo?.organizationName ?? '',
      organizationStatus: this.integrationInfo?.enabled || false,
      organizationPartnerIds: newCustomerIds,
      organizationId: this.integrationInfo?.organizationId || '',
    };

    this.integrationService.updateIntegration(integrationParams)
      .pipe(finalize(() => {
        this.isLoading = false;
        this.openAssociateCustomerDialog = false;
      }))
      .subscribe((success) => {
        if (success) {
          this.integrationInfo!.customerIds = newCustomerIds;

          nonAssociatedCustomers.forEach(customer => {
            Object.assign(customer['associationBadge'], associationBadge(true));
          });

          this.applyAssociatedFilter();
          this.rebuildAssociatedCustomerOptions();
          this.buildToastMessage(`Successfully associated ${customerIdsToAdd.length} customer(s)`, 'success');
        } else {
          this.buildToastMessage('Failed to associate customers', 'error');
        }
      });
  }

  /**
   * The row's … menu (Kohl, 2026-10-16): Associate or Disassociate, and Set Active or Set Disabled, by the row's state.
   * Empty for anyone who can't act, which hides the row's menu (aw-table re-reads it per row).
   */
  rowActions = (row: TableRow): TableActionMenu[] => {
    const customer = row as CustomerRow;
    return associationRowActions(customer, {
      canAssociate: this.userPermissions['canEdit'],
      canDisassociate: this.userPermissions['canDelete'],
      canSetStatus: this.userPermissions['canEdit'],
      associate: () => this.openAssociate([row]),
      disassociate: () => {
        this.singleCustomerToDelete = row;
        this.openDeleteCustomerDialogSingle = true;
      },
      setStatus: (enabled) => this.setCustomerStatus(customer, enabled),
    });
  };

  /** Associate, from the bar (checked rows) or a row's menu: confirms, then associateCustomersFormSubmit. */
  openAssociate(rows: TableRow[]): void {
    this.rowsToAssociate = rows.filter((row) => !row['associationBadge']?.isAssociated);
    this.openAssociateCustomerDialog = true;
  }

  /** A row's Set Active / Set Disabled: applies at once and shows a toast. */
  setCustomerStatus(row: CustomerRow, enabled: boolean): void {
    const customerInfo = row.customerInfo;
    this.isLoading = true;
    this.customerService
      .updateCustomer({
        organizationAuthType: row.authType,
        organizationName: customerInfo.customerName,
        organizationStatus: enabled,
        organizationPartnerIds: customerInfo.customerIntegrationPartnerIDs,
        organizationId: customerInfo.customerId,
      })
      // CustomerService already maps a failed request to false.
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe((updated) => {
        const state = enabled ? 'Active' : 'Disabled';
        if (!updated) {
          this.buildToastMessage(`Failed to set ${customerInfo.customerName} to ${state}`, 'error');
          return;
        }
        Object.assign(row.badgeNested, statusBadge(enabled));
        this.applyAssociatedFilter();
        this.buildToastMessage(`${customerInfo.customerName} set to ${state}`, 'success');
      });
  }
}
